// Core types for intelligent scheduling system
// This provides the foundation for the AI-powered scheduling features

export interface UserContext {
  currentLocation?: { latitude: number; longitude: number };
  timeZone: string;
  workingHours: WorkingHours;
  historicalCompletionData: CompletionPattern[];
  currentDateTime: Date;
  preferredWorkStyles?: ('focused' | 'distributed' | 'morning' | 'evening')[];
  productivityMetrics?: ProductivityMetrics;
  environmentalFactors?: EnvironmentalFactors;
}

export interface WorkingHours {
  monday: { start: string; end: string; enabled: boolean };
  tuesday: { start: string; end: string; enabled: boolean };
  wednesday: { start: string; end: string; enabled: boolean };
  thursday: { start: string; end: string; enabled: boolean };
  friday: { start: string; end: string; enabled: boolean };
  saturday: { start: string; end: string; enabled: boolean };
  sunday: { start: string; end: string; enabled: boolean };
}

export interface CompletionPattern {
  timeOfDay: number; // 1-24 hour
  dayOfWeek: number; // 1-7
  completionRate: number; // 0-1
  averageItemsCompleted: number;
  averageFocusTime: number; // minutes
  taskTypes: string[]; // categories of tasks completed
  contextualSuccess: ContextualSuccessData;
}

export interface ContextualSuccessData {
  locationSuccessRate: { [location: string]: number };
  timeBlockSuccessRate: { [timeBlock: string]: number }; // 'morning', 'afternoon', 'evening'
  deadlinePressureResponse: { [pressure: string]: number }; // 'low', 'medium', 'high'
}

export interface ProductivityMetrics {
  averageTaskCompletionTime: number; // minutes
  focusSessionLength: number; // minutes
  optimalTaskSwitchingInterval: number; // minutes  
  peakProductivityHours: number[]; // hours of day when user is most productive
  burnoutIndicators: BurnoutIndicator[];
}

export interface BurnoutIndicator {
  type: 'consecutive_days' | 'daily_overload' | 'missed_deadlines' | 'declining_quality';
  severity: 'low' | 'medium' | 'high';
  recommendation: string;
}

export interface EnvironmentalFactors {
  weatherSensitivity?: boolean;
  noiseToleranceLevel?: 'low' | 'medium' | 'high';
  lightingPreference?: 'natural' | 'artificial' | 'dim';
  socialInteractionImpact?: 'positive' | 'negative' | 'neutral';
}

export interface ScheduleRecommendation {
  listId: string;
  recommendedTimeSlots: TimeSlot[];
  primaryRecommendation: TimeSlot;
  confidence: number; // 0-1
  reasoning: string[];
  estimatedCompletionProbability: number;
  alternativeSlots: TimeSlot[];
  contextFactors: ContextFactors;
  adaptabilityScore: number; // How flexible this recommendation is
}

export interface TimeSlot {
  startTime: Date;
  endTime: Date;
  optimalDuration: number; // minutes
  buffer: number; // buffer time in minutes
  focusRequirement: 'low' | 'medium' | 'high';
  locationSuitability?: string[];
}

export interface ContextFactors {
  timeOptimality: number; // 0-1
  deadlineUrgency: number; // 0-1
  taskComplexity: number; // 0-1
  historicalSuccess: number; // 0-1
  environmentalFit: number; // 0-1
  workloadBalance: number; // 0-1
}

export interface IntelligentSchedule {
  id: string;
  listId: string;
  
  // Core scheduling data
  primaryTimeSlot: TimeSlot;
  alternativeTimeSlots: TimeSlot[];
  confidence: number; // 0-1 confidence in this schedule
  reasoning: string[]; // Why this time was chosen
  contextFactors: ContextFactors;
  
  // Intelligence metadata
  isAutoGenerated: boolean;
  lastOptimized: Date;
  adaptivePatterns: AdaptivePattern[];
  learningData: ScheduleLearningData;
  
  // Status and tracking
  status: 'pending' | 'accepted' | 'modified' | 'rejected' | 'completed';
  userModifications?: UserModification[];
  completionFeedback?: CompletionFeedback;
  
  // Notification settings
  smartNotifications: SmartNotification[];
  
  // Optimization data
  optimizationScore: number; // Overall optimization score
  constraintViolations: ConstraintViolation[];
}

export interface AdaptivePattern {
  patternType: 'completion_time' | 'energy_level' | 'focus_duration' | 'task_switching' | 'break_timing' | 'deadline_response';
  value: number;
  confidence: number;
  lastUpdated: Date;
  trendDirection: 'improving' | 'declining' | 'stable';
  sampleSize: number; // Number of data points this pattern is based on
}

export interface ScheduleLearningData {
  acceptanceRate: number; // How often user accepts recommendations
  modificationPatterns: string[]; // Common modifications user makes
  rejectionReasons: string[]; // Why user rejects recommendations
  successMetrics: SuccessMetrics;
  feedbackHistory: CompletionFeedback[];
}

export interface SuccessMetrics {
  onTimeCompletionRate: number;
  qualityScore: number; // Based on user feedback
  stressLevel: number; // Reported stress during completion
  satisfactionScore: number; // User satisfaction with timing
}

export interface UserModification {
  type: 'time_change' | 'duration_change' | 'break_addition' | 'split_task' | 'reschedule';
  originalValue: any;
  modifiedValue: any;
  timestamp: Date;
  reason?: string;
}

export interface CompletionFeedback {
  scheduleId: string;
  actualCompletionTime: Date;
  actualDuration: number;
  qualityRating: 1 | 2 | 3 | 4 | 5; // User-reported quality
  stressLevel: 1 | 2 | 3 | 4 | 5; // User-reported stress
  satisfactionRating: 1 | 2 | 3 | 4 | 5; // Satisfaction with timing
  wouldUseAgain: boolean;
  improvements?: string; // User suggestions
  contextNotes?: string; // Environmental factors that affected completion
}

export interface SmartNotification {
  id: string;
  scheduleId: string;
  
  // Timing
  triggerTime: Date;
  contextTrigger: 'optimal_time' | 'deadline_urgency' | 'energy_peak' | 'completion_opportunity';
  dynamicTiming: boolean; // Whether timing adjusts based on context
  
  // Content
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  
  // Personalization
  personalizationData: {
    historicalResponseRate: number;
    preferredNotificationTime: number; // hour of day
    maxDailyNotifications: number;
    notificationFatigueScore: number; // 0-1
  };
  
  // Intelligence
  contextualFactors: string[];
  confidence: number;
  alternativeTimes: Date[];
  
  // Tracking
  status: 'pending' | 'sent' | 'dismissed' | 'acted_upon' | 'snoozed';
  userResponse?: NotificationResponse;
}

export interface NotificationResponse {
  action: 'accept' | 'snooze' | 'reschedule' | 'dismiss';
  responseTime: Date;
  snoozeUntil?: Date;
  rescheduleRequest?: Date;
  feedback?: string;
}

export interface ConstraintViolation {
  type: 'time_conflict' | 'energy_mismatch' | 'workload_overflow' | 'location_unsuitable' | 'break_insufficient';
  severity: 'minor' | 'moderate' | 'major' | 'critical';
  description: string;
  suggestedResolution: string;
  canAutoResolve: boolean;
}

export interface SchedulingConstraints {
  maxDailyHours?: number;
  preferredTimeBlocks?: string[];
  avoidTimeBlocks?: string[];
  minimumBreakBetweenTasks?: number; // minutes
  maximumConsecutiveFocusTime?: number; // minutes
  locationConstraints?: string[];
}

// Events for tracking and learning
export interface CompletionEvent {
  id: string;
  listId: string;
  scheduleId?: string;
  completedAt: Date;
  duration: number; // actual duration in minutes
  quality: number; // 1-5 rating
  context: {
    location?: { latitude: number; longitude: number };
    timeOfDay: number;
    dayOfWeek: number;
    wasScheduled: boolean;
    distractionLevel: number; // 1-5
    energyLevel: number; // 1-5
  };
}

// In-app notification types
// Notification categories for logical grouping
export enum NotificationCategory {
  TASK_ACTION = 'task_action',      // Notifications requiring task actions
  INFORMATION = 'information',      // Informational only notifications
  REMINDER = 'reminder',            // Simple reminders
  ACHIEVEMENT = 'achievement',      // Completion/success notifications
  WARNING = 'warning'               // Deadline/urgent notifications
}

// Extended notification types
export type NotificationType = 
  | 'schedule_reminder' 
  | 'deadline_warning' 
  | 'task_suggestion' 
  | 'break_reminder'
  | 'list_complete'
  | 'item_complete'
  | 'daily_summary';

export interface InAppNotification {
  id: string;
  scheduleId: string;
  type: NotificationType;
  
  // Display
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  
  // Timing
  triggerTime: Date;
  createdAt: Date;
  expiresAt: Date;
  
  // State
  isRead: boolean;
  isPersistent: boolean; // Whether to keep until user interacts
  
  // Intelligence data
  contextualFactors: string[];
  confidence: number;
  
  // Actions
  actions: NotificationAction[];
  
  // UI metadata
  iconType?: 'clock' | 'alert' | 'target' | 'calendar' | 'check-circle' | 'check' | 'lightbulb' | 'coffee' | 'bar-chart' | 'bell';
  color?: 'blue' | 'yellow' | 'red' | 'green' | 'purple';
  dismissible?: boolean;
}

export interface NotificationAction {
  id: string;
  label: string;
  action: string;
  icon?: string;  // Add icon support
  variant?: 'primary' | 'secondary' | 'danger';
  data?: any;
}

// Notification configuration
export interface NotificationConfig {
  type: string;
  category: NotificationCategory;
  defaultActions: NotificationAction[];
  allowSnooze: boolean;
  snoozeOptions?: number[]; // minutes
  autoCloseTime?: number;   // milliseconds, 0 = never
}

export interface SchedulingAnalytics {
  totalSchedules: number;
  activeSchedules: number;
  averageConfidence: number;
  acceptanceRate: number;
  completionRate: number;
  userSatisfaction: number;
  learningProgress: {
    patternAccuracy: number;
    recommendationImprovement: number;
    dataPoints: number;
  };
  optimizationImpact: {
    timesSaved: number; // minutes per week
    stressReduction: number; // 0-1
    productivityGain: number; // percentage
  };
}